<template>
  <div class="flex justify-between items-center gap-4">
    <SettingsOption
      label="Condition"
      tooltip="Condition required to make the rule active"
      class="w-auto"
      labelClass="justify-start"
    >
      <ListSelect
        class="w-52"
        :modelValue="conditionType(cond)"
        @update:modelValue="updateConditionType"
        :options="CONDITION_TYPE.filter((c) => !isCondTypeLogic(c))"
        :clearable="false"
      />
    </SettingsOption>

    <template
      v-if="
        isCondAlways(cond) ||
        isCondUsbOn(cond) ||
        isCondPressed(cond) ||
        isCondBootloaderAllowed(cond)
      "
    ></template>
    <!-- label="UsbOn" tooltip="Apply if USB is connected/disconnected" -->
    <!-- label="Pressed" tooltip="Apply when this key has given pressed state" -->

    <SettingsOption
      v-else-if="isCondLed(cond)"
      class="w-full"
      label="Led"
      tooltip="Applies this rule if host PC specifies that given LED is on"
    >
      <ListSelect
        class="min-w-[15ch] w-full"
        :options="KEYBOARD_LEDS"
        v-model="cond.Led"
        :clearable="false"
      />
    </SettingsOption>

    <SettingsOption
      v-else-if="isCondRole(cond)"
      class="w-full"
      label="Role"
      tooltip="Apply if the keyboard half has given role"
    >
      <ListSelect
        class="min-w-[14ch] w-full"
        :options="['Master', 'Slave']"
        v-model="cond.Role"
        :clearable="false"
      />
    </SettingsOption>

    <SettingsOption
      v-else-if="isCondKeyAction(cond)"
      class="w-full"
      label="Action"
      tooltip="Apply to keys with given action type"
    >
      <ListSelect
        class="min-w-[21ch] w-full"
        :options="KEY_ACTION"
        v-model="cond.KeyAction"
        :clearable="false"
        :getOptionLabel="keyActionText"
      />
    </SettingsOption>

    <div
      v-else-if="isCondKeyPressed(cond)"
      class="flex justify-between items-center"
    >
      <span class="px-2">Row</span>
      <IntegerInput
        class="w-[6ch] input-sm"
        :min="0"
        :max="kb.rows - 1"
        v-model="cond.KeyPressed[0]"
      />

      <span class="px-2">Col</span>
      <IntegerInput
        class="w-[6ch] input-sm"
        :min="0"
        :max="kb.columns - 1"
        v-model="cond.KeyPressed[1]"
      />

      <PopupButton ref="popupButton" placement="auto" :offset="3">
        <template #button>
          <div class="ml-1 tooltip" data-tip="Select on keyboard">
            <div class="btn btn-sm btn-ghost">
              <Icon icon="material-symbols:keyboard-alt-outline" />
            </div>
          </div>
        </template>
        <template #popup>
          <div
            class="relative m-4"
            :style="{ width: area_width + 'px', height: area_height + 'px' }"
          >
            <template v-for="(k, id) in keys" :key="id">
              <KeyCap
                :row="k.row"
                :col="k.col"
                :x="k.key.x"
                :y="k.key.y"
                :rot="k.key.rot"
                :height="k.key.size.y"
                :width="k.key.size.x"
                :blank="true"
                :outlined="
                  cond.KeyPressed[0] == k.row && cond.KeyPressed[1] == k.col
                "
                @click="keySelected(k.row, k.col)"
              />
            </template>
          </div>
        </template>
      </PopupButton>
    </div>

    <SettingsOption
      v-else-if="isCondLayer(cond)"
      class="w-full"
      label="Layer"
      tooltip="Apply when on a given layer"
    >
      <ListSelect
        class="min-w-[12ch] w-full"
        :options="kb.availableLayers"
        v-model="cond.Layer"
        :clearable="false"
      />
    </SettingsOption>

    <template v-else>{{ assertUnreachable(cond) }}</template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { assertUnreachable } from "@/utils";
import {
  ConditionValue,
  ConditionTypeValue,
  CONDITION_TYPE,
  conditionType,
  isCondAlways,
  isCondLed,
  isCondRole,
  isCondUsbOn,
  isCondPressed,
  isCondKeyAction,
  isCondKeyPressed,
  isCondLayer,
  isCondBootloaderAllowed,
  isCondTypeLogic,
  KEYBOARD_LEDS,
  KEY_ACTION,
} from "@/keyboard/leds";
import { layout_bounds } from "@/keyboard/layout";
import { useKeyboardStore, useLayoutStore } from "@/stores";
import KeyCap from "@/components/keys/KeyCap.vue";
import SettingsOption from "@/components/utils/SettingsOption.vue";
import IntegerInput from "@/components/utils/IntegerInput.vue";
import ListSelect from "@/components/utils/ListSelect.vue";
import PopupButton from "@/components/utils/PopupButton.vue";
import { KeyAction } from "@/keyboard";

const props = defineProps<{
  modelValue: ConditionValue;
}>();

const emit = defineEmits<{
  (_e: "update:modelValue", _value: ConditionValue): void;
}>();

const kb = useKeyboardStore();
const layout = useLayoutStore();

const popupButton = ref<InstanceType<typeof PopupButton> | null>(null);

const cond = computed(() => props.modelValue);

const size = { width: 600, height: 2000 };
const keys = computed(() => {
  const transform = layout.layout.fit_size({
    x: size.width,
    y: size.height,
  });
  return layout.layout.iter_transform(transform);
});

const area_size = computed(() => {
  const [area_min, area_max] = layout_bounds(keys.value);
  const area_width = area_max.x - area_min.x;
  const area_height = area_max.y - area_min.y;
  return {
    x: area_width,
    y: area_height,
  };
});

const area_width = computed(() => area_size.value.x);
const area_height = computed(() => area_size.value.y);

const DEFAULTS: Record<ConditionTypeValue, ConditionValue> = {
  Always: "Always",
  Led: { Led: "CapsLock" },
  UsbOn: "UsbOn",
  Role: { Role: "Master" },
  Pressed: "Pressed",
  KeyAction: { KeyAction: "Trans" },
  // TODO: warn about invalid key/layer
  KeyPressed: { KeyPressed: [0, 0] },
  Layer: { Layer: 0 },
  BootloaderAllowed: "BootloaderAllowed",
};

const keyActionText = (a: KeyAction): string =>
  a == "Trans"
    ? "Transparent"
    : a == "DefaultLayer"
    ? "Default Layer"
    : a == "MultipleActions"
    ? "Multiple Actions"
    : a == "MultipleKeyCodes"
    ? "Multiple Keycodes"
    : a;

const updateConditionType = (c: ConditionTypeValue) =>
  emit("update:modelValue", DEFAULTS[c]);

const keySelected = (row: number, col: number) => {
  if (isCondKeyPressed(cond.value)) {
    emit("update:modelValue", { KeyPressed: [row, col] });
    popupButton.value?.show(false);
  }
};
</script>
